import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class RecommendedAutomationsService {

  constructor(private http: HttpClient) { }

  /**
   * Load template data - action_code yz4utp. Payload includes activity_key, chart_filter, screen_key, etc.
   */
  loadTemplateData(
    baseUrl: string,
    globalService: any,
    filterParams: any,
    rowData: any
  ): Observable<any> {
    const g = globalService?.globalParams || globalService || {};
    const f = filterParams || {};
    const sysAcct = rowData?.TRIGGER_CHART_KEY != null && rowData?.POSTING_ACCT_KEY != null && rowData?.POSTING_TAC_KEY != null
      ? `${rowData.TRIGGER_CHART_KEY}_${rowData.POSTING_ACCT_KEY}-${rowData.POSTING_TAC_KEY}`
      : '';
    const chartFilter = f.chart_filter ?? g.chart_filter ?? rowData?.chart_filter ?? '';
    const params: Record<string, string> = {
      action_code: 'yz4utp',
      tax_year: String(f.tax_year ?? g.tax_year ?? rowData?.TAX_YEAR ?? ''),
      scenario: String(f.scenario ?? g.scenario ?? rowData?.SCENARIO ?? ''),
      jcd_key: String(f.jcd_key ?? g.jcd_key ?? rowData?.JCD_KEY ?? '250'),
      sys_acct: sysAcct,
      activity_key: String(f.activity_key ?? g.activity_key ?? rowData?.activity_key ?? ''),
      chart_filter: chartFilter,
      is_issue_key_enabled: String(f.is_issue_key_enabled ?? g.is_issue_key_enabled ?? 'N'),
      is_locked: String(f.is_locked ?? g.is_locked ?? 'N'),
      issue_id: String(f.issue_id ?? g.issue_id ?? ''),
      issue_id_for_tx_save: String(f.issue_id_for_tx_save ?? g.issue_id_for_tx_save ?? ''),
      issue_short_desc: String(f.issue_short_desc ?? g.issue_short_desc ?? ''),
      jcd_ta_key: String(f.jcd_ta_key ?? g.jcd_ta_key ?? rowData?.jcd_ta_key ?? ''),
      scenarioCode: String(f.scenarioCode ?? g.scenarioCode ?? f.scenario_code ?? g.scenario_code ?? 'RAF'),
      screen_key: String(f.screen_key ?? g.screen_key ?? rowData?.screen_key ?? ''),
      screen_type: String(f.screen_type ?? g.screen_type ?? 'grid')
    };
    const url = `${baseUrl}/loadJsonObject`;
    return this.http.get<any>(url, { params, withCredentials: true });
  }

  /**
   * Load SchdM type options - action_code jhsi7h. Payload includes activity_key, chart_filter, screen_key, etc.
   */
  loadSchdmType(
    baseUrl: string,
    globalService: any,
    filterParams: any,
    rowData: any,
    schdmTemp: string
  ): Observable<any> {
    const g = globalService?.globalParams || globalService || {};
    const f = filterParams || {};
    const sysAcct = rowData?.LOCAL_CHART_KEY != null && rowData?.LOCAL_ACCT_KEY != null && rowData?.SYSTEM_ACCT_KEY != null
      ? `${rowData.LOCAL_CHART_KEY}_${rowData.LOCAL_ACCT_KEY}-${rowData.SYSTEM_ACCT_KEY}`
      : '';
    const chartFilter = f.chart_filter ?? g.chart_filter ?? rowData?.chart_filter ?? '';
    const params: Record<string, string> = {
      action_code: 'jhsi7h',
      tax_year: String(f.tax_year ?? g.tax_year ?? rowData?.TAX_YEAR ?? ''),
      scenario: String(f.scenario ?? g.scenario ?? rowData?.SCENARIO ?? ''),
      jcd_key: String(f.jcd_key ?? g.jcd_key ?? rowData?.JCD_KEY ?? '250'),
      sys_acct: sysAcct,
      schdm_temp: schdmTemp || '',
      activity_key: String(f.activity_key ?? g.activity_key ?? rowData?.activity_key ?? ''),
      chart_filter: chartFilter,
      is_issue_key_enabled: String(f.is_issue_key_enabled ?? g.is_issue_key_enabled ?? 'N'),
      is_locked: String(f.is_locked ?? g.is_locked ?? 'N'),
      issue_id: String(f.issue_id ?? g.issue_id ?? ''),
      issue_id_for_tx_save: String(f.issue_id_for_tx_save ?? g.issue_id_for_tx_save ?? ''),
      issue_short_desc: String(f.issue_short_desc ?? g.issue_short_desc ?? ''),
      jcd_ta_key: String(f.jcd_ta_key ?? g.jcd_ta_key ?? rowData?.jcd_ta_key ?? ''),
      scenarioCode: String(f.scenarioCode ?? g.scenarioCode ?? f.scenario_code ?? g.scenario_code ?? 'RAF'),
      screen_key: String(f.screen_key ?? g.screen_key ?? rowData?.screen_key ?? ''),
      screen_type: String(f.screen_type ?? g.screen_type ?? 'grid')
    };
    const url = `${baseUrl}/loadJsonObject`;
    return this.http.get<any>(url, { params, withCredentials: true });
  }

  /**
   * Load TBBS account options - action_code wfk6zt. Payload includes activity_key, chart_filter, screen_key, etc.
   */
  loadTbbsData(
    baseUrl: string,
    globalService: any,
    filterParams: any,
    rowData: any,
    schdmTemp: string,
    schdmTypeKey: string
  ): Observable<any> {
    const g = globalService?.globalParams || globalService || {};
    const f = filterParams || {};
    const sysAcct = rowData?.TRIGGER_CHART_KEY != null && rowData?.TRIGGER_ACCT_KEY != null && rowData?.POSTING_TAC_KEY != null
      ? `${rowData.TRIGGER_CHART_KEY}_${rowData.TRIGGER_ACCT_KEY}-${rowData.POSTING_TAC_KEY}`
      : '';
    const chartFilter = f.chart_filter ?? g.chart_filter ?? rowData?.chart_filter ?? '';
    const params: Record<string, string> = {
      action_code: 'wfk6zt',
      tax_year: String(f.tax_year ?? g.tax_year ?? rowData?.TAX_YEAR ?? ''),
      scenario: String(f.scenario ?? g.scenario ?? rowData?.SCENARIO ?? ''),
      jcd_key: String(f.jcd_key ?? g.jcd_key ?? rowData?.JCD_KEY ?? '250'),
      sys_acct: sysAcct,
      local_accts: String(rowData?.TRIGGER_ACCT_KEY ?? ''),
      schdm_temp: schdmTemp || '',
      schdm_type: schdmTypeKey || '',
      schdm_method: String(rowData?.METHOD_OF_CALC ?? ''),
      source_system: 'D',
      activity_key: String(f.activity_key ?? g.activity_key ?? rowData?.activity_key ?? ''),
      chart_filter: chartFilter,
      is_issue_key_enabled: String(f.is_issue_key_enabled ?? g.is_issue_key_enabled ?? 'N'),
      is_locked: String(f.is_locked ?? g.is_locked ?? 'N'),
      issue_id: String(f.issue_id ?? g.issue_id ?? ''),
      issue_id_for_tx_save: String(f.issue_id_for_tx_save ?? g.issue_id_for_tx_save ?? ''),
      issue_short_desc: String(f.issue_short_desc ?? g.issue_short_desc ?? ''),
      jcd_ta_key: String(f.jcd_ta_key ?? g.jcd_ta_key ?? rowData?.jcd_ta_key ?? ''),
      scenarioCode: String(f.scenarioCode ?? g.scenarioCode ?? f.scenario_code ?? g.scenario_code ?? 'RAF'),
      screen_key: String(f.screen_key ?? g.screen_key ?? rowData?.screen_key ?? ''),
      screen_type: String(f.screen_type ?? g.screen_type ?? 'grid')
    };
    const url = `${baseUrl}/loadJsonObject`;
    return this.http.get<any>(url, { params, withCredentials: true });
  }

  /**
   * Save recommended automation - action_code zsw36r, process_name SAVE_RECOMM_AUTOMATIONS.
   * Payload matches backend expectation: jsonSettings, jsonObj, tax_year, screen_key, scenario, jcd_key, etc.
   */
  saveRecommAutomation(
    baseUrl: string,
    payload: any[],
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=zsw36r`;
    const g = globalService || {};
    const f = filterParams || {};
    const data = Array.isArray(payload) && payload.length > 0 ? payload[0] : rowData;

    const jsonSettings = JSON.stringify(payload || []);
    const jsonObj = JSON.stringify(payload || []);
    const chartFilter = data?.TRIGGER_CHART_KEY != null ? String(data.TRIGGER_CHART_KEY) : '';

    const params: Record<string, string> = {
      jsonSettings,
      jsonObj,
      tax_year: String(f.tax_year ?? g.tax_year ?? rowData?.TAX_YEAR ?? data?.TAX_YEAR ?? ''),
      screen_key: String(f.screen_key ?? g.screen_key ?? ''),
      scenario: String(f.scenario ?? g.scenario ?? rowData?.SCENARIO ?? data?.SCENARIO ?? ''),
      jcd_key: String(f.jcd_key ?? g.jcd_key ?? rowData?.JCD_KEY ?? data?.JCD_KEY ?? '250'),
      jcd_ta_key: String(f.jcd_ta_key ?? g.jcd_ta_key ?? ''),
      activity_key: String(f.activity_key ?? g.activity_key ?? ''),
      screen_type: 'grid',
      is_locked: 'N',
      issue_id: String(f.issue_id ?? g.issue_id ?? ''),
      issue_short_desc: String(f.issue_short_desc ?? g.issue_short_desc ?? ''),
      issue_id_for_tx_save: String(f.issue_id_for_tx_save ?? g.issue_id_for_tx_save ?? ''),
      scenarioCode: 'RAF',
      is_issue_key_enabled: 'N',
      checkLockedbj: '[{}]',
      chart_filter: chartFilter,
      process_name: 'SAVE_RECOMM_AUTOMATIONS'
    };

    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key]);
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }
}
